import React, { useEffect, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import {
  ReportItem,
  ReportMode,
  Severity,
  DragItem,
  Photo,
  CostSuggestion,
} from '@/types';
import { costCatalogApi } from '@/api';
import { getSeverityLabel, formatCostRange } from '@/utils/helpers';
import { LANGUAGE_CONFIGS } from '@/utils/constants';
import { severityKey } from '@/ui/severity';
import type { SeverityKey } from '@/ui/severity';
import { useAutoSaveContext } from '@/hooks/useReportAutoSave';
import PhotoUpload from '../PhotoUpload';
import {
  IconCamera,
  IconCopy,
  IconGrip,
  IconTrash,
} from './_icons';
import { ItemTypes } from './_types';

interface ItemCardProps {
  item: ReportItem;
  index: number;
  mode: ReportMode;
  propertyZip?: string;
  sectionName?: string;
  onUpdate: (updates: Partial<ReportItem>) => Promise<void>;
  onPhotosUpdate: (photos: Photo[]) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onMoveItem: (dragIndex: number, hoverIndex: number) => void;
}

const formatCostChip = (item: ReportItem): string | null => {
  if (item.estimatedCostLow != null && item.estimatedCostHigh != null) {
    return formatCostRange(item.estimatedCostLow, item.estimatedCostHigh);
  }
  if (item.estimatedCost != null && item.estimatedCost > 0) {
    return `$${item.estimatedCost.toLocaleString()}`;
  }
  return null;
};

const severityModClass = (sev: Severity): string => {
  const k = severityKey(sev);
  if (k === 'safety_concern') return 're-item-sev--safety';
  if (k === 'recommendation') return 're-item-sev--recommend';
  return 're-item-sev--none';
};

const GripIcon: React.FC = () => <IconGrip size={14} />;

export const ItemCard: React.FC<ItemCardProps> = ({
  item, index, mode, propertyZip, sectionName,
  onUpdate, onPhotosUpdate, onDelete, onDuplicate, onMoveItem,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<CostSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [costSearchTerm, setCostSearchTerm] = useState('');
  const suggestionsRef = React.useRef<HTMLDivElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const dragHandleRef = React.useRef<HTMLButtonElement>(null);

  const [{ isDragging }, drag, dragPreview] = useDrag({
    type: ItemTypes.ITEM,
    item: { id: item.id, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: ItemTypes.ITEM,
    hover: (dragItem: DragItem) => {
      if (!containerRef.current) return;
      const dragIndex = dragItem.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;
      onMoveItem(dragIndex, hoverIndex);
      dragItem.index = hoverIndex;
    },
  });

  drag(dragHandleRef);
  drop(dragPreview(containerRef));

  const [localTitle, setLocalTitle] = useState(item.title);
  const [localDescription, setLocalDescription] = useState(item.description);
  const [localLocation, setLocalLocation] = useState(item.location || '');
  const [localCostLow, setLocalCostLow] = useState(
    item.estimatedCostLow != null ? String(item.estimatedCostLow) : ''
  );
  const [localCostHigh, setLocalCostHigh] = useState(
    item.estimatedCostHigh != null ? String(item.estimatedCostHigh) : ''
  );

  const autoSave = useAutoSaveContext();
  const scheduleSave = autoSave?.scheduleSave;
  // Ref the parent `onUpdate` so the auto-save effects don't list it as a
  // dependency — the inline arrow in the parent is recreated every render,
  // which would otherwise re-fire each effect on every save-state change.
  const onUpdateRef = useRef(onUpdate);
  useEffect(() => { onUpdateRef.current = onUpdate; });

  // Track the last prop value we synced from. Lets each prop→state sync
  // skip when the user has typed since (otherwise an in-flight save echoing
  // back the older value would clobber unsaved keystrokes).
  const lastTitleRef = useRef(item.title);
  const lastDescriptionRef = useRef(item.description);
  const lastLocationRef = useRef(item.location || '');
  const lastCostLowRef = useRef(
    item.estimatedCostLow != null ? String(item.estimatedCostLow) : ''
  );
  const lastCostHighRef = useRef(
    item.estimatedCostHigh != null ? String(item.estimatedCostHigh) : ''
  );

  useEffect(() => {
    if (localTitle === lastTitleRef.current) setLocalTitle(item.title);
    lastTitleRef.current = item.title;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.title]);
  useEffect(() => {
    if (localDescription === lastDescriptionRef.current) setLocalDescription(item.description);
    lastDescriptionRef.current = item.description;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.description]);
  useEffect(() => {
    const next = item.location || '';
    if (localLocation === lastLocationRef.current) setLocalLocation(next);
    lastLocationRef.current = next;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.location]);
  useEffect(() => {
    const next = item.estimatedCostLow != null ? String(item.estimatedCostLow) : '';
    if (localCostLow === lastCostLowRef.current) setLocalCostLow(next);
    lastCostLowRef.current = next;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.estimatedCostLow]);
  useEffect(() => {
    const next = item.estimatedCostHigh != null ? String(item.estimatedCostHigh) : '';
    if (localCostHigh === lastCostHighRef.current) setLocalCostHigh(next);
    lastCostHighRef.current = next;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.estimatedCostHigh]);

  // Debounced auto-save effects — one per editable text/cost field. Each
  // schedules a PATCH only when the local value diverges from the server
  // value (so mount + post-save echo are no-ops). The autoSave context
  // collapses rapid typing into a single PATCH per ~1.5s.
  useEffect(() => {
    if (!scheduleSave) return;
    if (localTitle === item.title) return;
    if (!localTitle.trim()) return;
    scheduleSave(`item:${item.id}:title`, async () => {
      await onUpdateRef.current({ title: localTitle });
    });
  }, [localTitle, item.title, item.id, scheduleSave]);

  useEffect(() => {
    if (!scheduleSave) return;
    if (localDescription === item.description) return;
    scheduleSave(`item:${item.id}:description`, async () => {
      await onUpdateRef.current({ description: localDescription });
    });
  }, [localDescription, item.description, item.id, scheduleSave]);

  useEffect(() => {
    if (!scheduleSave) return;
    if (localLocation === (item.location || '')) return;
    scheduleSave(`item:${item.id}:location`, async () => {
      await onUpdateRef.current({ location: localLocation });
    });
  }, [localLocation, item.location, item.id, scheduleSave]);

  useEffect(() => {
    if (!scheduleSave) return;
    const serverValue = item.estimatedCostLow != null ? String(item.estimatedCostLow) : '';
    if (localCostLow === serverValue) return;
    scheduleSave(`item:${item.id}:costLow`, async () => {
      const parsed = localCostLow ? parseFloat(localCostLow) : undefined;
      if (parsed != null && Number.isNaN(parsed)) return;
      const high = localCostHigh ? parseFloat(localCostHigh) : undefined;
      const mid = parsed != null && high != null ? Math.round((parsed + high) / 2) : parsed;
      await onUpdateRef.current({ estimatedCostLow: parsed, estimatedCost: mid });
    });
  }, [localCostLow, localCostHigh, item.estimatedCostLow, item.id, scheduleSave]);

  useEffect(() => {
    if (!scheduleSave) return;
    const serverValue = item.estimatedCostHigh != null ? String(item.estimatedCostHigh) : '';
    if (localCostHigh === serverValue) return;
    scheduleSave(`item:${item.id}:costHigh`, async () => {
      const parsed = localCostHigh ? parseFloat(localCostHigh) : undefined;
      if (parsed != null && Number.isNaN(parsed)) return;
      const low = localCostLow ? parseFloat(localCostLow) : undefined;
      const mid = parsed != null && low != null ? Math.round((low + parsed) / 2) : parsed;
      await onUpdateRef.current({ estimatedCostHigh: parsed, estimatedCost: mid });
    });
  }, [localCostHigh, localCostLow, item.estimatedCostHigh, item.id, scheduleSave]);

  const costChipLabel = formatCostChip(item);

  const fetchSuggestions = async (query: string) => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }
    try {
      const response = await costCatalogApi.getSuggestions(query, propertyZip);
      if (response.success && response.data) {
        setSuggestions(response.data);
      }
    } catch {
      // Suggestions are optional
    }
  };

  const handleCostSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCostSearchTerm(value);
    setShowSuggestions(true);
    fetchSuggestions(value || item.title);
  };

  const handleCostFocus = () => {
    setShowSuggestions(true);
    if (suggestions.length === 0) {
      fetchSuggestions(costSearchTerm || item.title);
    }
  };

  const applySuggestion = (suggestion: CostSuggestion) => {
    const midpoint = Math.round((suggestion.costLow + suggestion.costHigh) / 2);
    setLocalCostLow(String(suggestion.costLow));
    setLocalCostHigh(String(suggestion.costHigh));
    onUpdate({
      estimatedCost: midpoint,
      estimatedCostLow: suggestion.costLow,
      estimatedCostHigh: suggestion.costHigh,
    });
    setShowSuggestions(false);
    setCostSearchTerm('');
  };

  const [aiSuggesting, setAiSuggesting] = useState(false);
  const [aiSuggestError, setAiSuggestError] = useState<string | null>(null);

  const handleAiSuggest = async () => {
    if (aiSuggesting) return;
    if (!item.title || !item.title.trim()) {
      setAiSuggestError('Add a title before asking for a suggestion.');
      return;
    }
    setAiSuggesting(true);
    setAiSuggestError(null);
    try {
      const response = await costCatalogApi.aiSuggest({
        title: item.title,
        description: item.description,
        severity: item.severity,
        sectionName: sectionName ?? null,
        propertyZip: propertyZip ?? null,
      });
      if (!response.success || !response.data) {
        setAiSuggestError(response.error || 'Suggestion failed');
        return;
      }
      const { costLow, costHigh } = response.data;
      const midpoint = Math.round((costLow + costHigh) / 2);
      setLocalCostLow(String(costLow));
      setLocalCostHigh(String(costHigh));
      await onUpdate({
        estimatedCost: midpoint,
        estimatedCostLow: costLow,
        estimatedCostHigh: costHigh,
      });
    } catch (err) {
      setAiSuggestError(err instanceof Error ? err.message : 'Suggestion failed');
    } finally {
      setAiSuggesting(false);
    }
  };

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const sevKey: SeverityKey = severityKey(item.severity);
  const severityOptions: { value: Severity; labelKey: SeverityKey; modClass: string }[] = [
    { value: null, labelKey: 'observation', modClass: 're-sev-opt--on-none' },
    { value: 'recommendation', labelKey: 'recommendation', modClass: 're-sev-opt--on-recommend' },
    { value: 'safety_concern', labelKey: 'safety_concern', modClass: 're-sev-opt--on-safety' },
  ];
  const langLabels = LANGUAGE_CONFIGS[mode].severityLabels;
  const phraseSuggestions = LANGUAGE_CONFIGS[mode].defaultPhrases[sevKey].slice(0, 3);

  const itemClass = [
    're-item',
    isOpen ? 're-item--open' : '',
    isDragging ? 're-item--dragging' : '',
  ].filter(Boolean).join(' ');

  return (
    <div ref={containerRef} className={itemClass}>
      <div
        className="re-item-row"
        onClick={(e) => {
          // Don't toggle when clicking on interactive elements inside the row
          const target = e.target as HTMLElement;
          if (target.closest('button, input, a, select, textarea')) return;
          setIsOpen(v => !v);
        }}
      >
        <button
          ref={dragHandleRef}
          className="re-item-grip"
          aria-label="Drag item"
          type="button"
          onClick={(e) => e.stopPropagation()}
        >
          <GripIcon />
        </button>
        <span
          className={`re-item-sev ${severityModClass(item.severity)}`}
          title={getSeverityLabel(item.severity, mode)}
          aria-label={getSeverityLabel(item.severity, mode)}
        />
        <input
          className="re-item-title-input"
          value={localTitle}
          onChange={(e) => setLocalTitle(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          aria-label="Item title"
        />
        <div className="re-item-meta">
          {item.aiGenerated && <span className="re-item-ai" title="AI-generated">AI</span>}
          {costChipLabel ? (
            <span className="re-cost-chip">Est. {costChipLabel}</span>
          ) : (
            <span className="re-cost-chip re-cost-chip--empty">No estimate</span>
          )}
          <span className={`re-photo-count ${item.photos.length === 0 ? 're-photo-count--empty' : ''}`}>
            <IconCamera size={13} />
            {item.photos.length}
          </span>
          <button
            className="re-icon-btn-sm"
            onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
            title="Duplicate item"
            aria-label="Duplicate item"
            type="button"
          >
            <IconCopy size={14} />
          </button>
          <button
            className="re-icon-btn-sm"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            title="Delete item"
            aria-label="Delete item"
            type="button"
          >
            <IconTrash size={14} />
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="re-item-body">
          <div className="re-field">
            <label className="re-label">Description</label>
            <textarea
              className="re-textarea"
              value={localDescription}
              onChange={(e) => setLocalDescription(e.target.value)}
              placeholder="Describe the finding…"
              rows={3}
            />
            {!localDescription && phraseSuggestions.length > 0 && (
              <div className="re-phrase-chips">
                <span className="re-phrase-chips-label">Try:</span>
                {phraseSuggestions.map((phrase) => (
                  <button
                    key={phrase}
                    type="button"
                    className="re-phrase-chip"
                    onClick={() => {
                      setLocalDescription(phrase);
                      onUpdate({ description: phrase });
                    }}
                  >
                    {phrase}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="re-item-body-row">
            <div className="re-field">
              <label className="re-label">Severity</label>
              <div className="re-sev-select" role="radiogroup" aria-label="Severity">
                {severityOptions.map(opt => {
                  const selected = (item.severity ?? null) === opt.value;
                  return (
                    <button
                      key={opt.labelKey}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      className={`re-sev-opt ${selected ? opt.modClass : ''}`}
                      onClick={() => onUpdate({ severity: opt.value })}
                    >
                      {opt.value === null ? '— None' : langLabels[opt.labelKey]}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="re-field" ref={suggestionsRef} style={{ position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                <label className="re-label">Cost low</label>
                <button
                  type="button"
                  onClick={handleAiSuggest}
                  disabled={aiSuggesting}
                  title="Suggest a low/high range with AI"
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--border-2)',
                    borderRadius: 'var(--radius-sm)',
                    padding: '2px 8px',
                    fontSize: 11,
                    fontFamily: 'var(--font-sans)',
                    color: aiSuggesting ? 'var(--fg-4)' : 'var(--brand-700)',
                    cursor: aiSuggesting ? 'wait' : 'pointer',
                    lineHeight: 1.4,
                  }}
                >
                  {aiSuggesting ? 'Thinking…' : 'Suggest with AI'}
                </button>
              </div>
              <input
                type="number"
                inputMode="numeric"
                className="re-input re-input--cost"
                value={localCostLow}
                onChange={(e) => setLocalCostLow(e.target.value)}
                onFocus={handleCostFocus}
                placeholder="0"
              />
              {aiSuggestError && (
                <div style={{ fontSize: 11, color: 'var(--safety-fg)', marginTop: 4 }}>
                  {aiSuggestError}
                </div>
              )}
              {showSuggestions && (
                <div style={{
                  position: 'absolute',
                  top: 'calc(100% + 4px)',
                  left: 0,
                  right: 0,
                  zIndex: 20,
                  background: '#fff',
                  border: '1px solid var(--border-2)',
                  borderRadius: 'var(--radius-md)',
                  boxShadow: 'var(--elev-overlay)',
                  padding: 4,
                }}>
                  <input
                    type="text"
                    value={costSearchTerm}
                    onChange={handleCostSearch}
                    placeholder="Search repair costs…"
                    className="re-input"
                    style={{ marginBottom: suggestions.length ? 4 : 0 }}
                  />
                  {suggestions.length > 0 && (
                    <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                      {suggestions.map((s) => (
                        <button
                          key={s.id}
                          onClick={() => applySuggestion(s)}
                          style={{
                            display: 'block',
                            width: '100%',
                            textAlign: 'left',
                            padding: '6px 8px',
                            background: 'transparent',
                            border: 'none',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: 12.5,
                            fontFamily: 'inherit',
                            color: 'var(--fg-1)',
                          }}
                          type="button"
                        >
                          <div style={{ fontWeight: 500 }}>{s.itemName}</div>
                          <div style={{ fontSize: 11, color: 'var(--fg-3)' }}>
                            {s.category} · {formatCostRange(s.costLow, s.costHigh)} {s.unit}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="re-field">
              <label className="re-label">Cost high</label>
              <input
                type="number"
                inputMode="numeric"
                className="re-input re-input--cost"
                value={localCostHigh}
                onChange={(e) => setLocalCostHigh(e.target.value)}
                placeholder="0"
              />
            </div>
          </div>

          <div className="re-field">
            <label className="re-label">Location</label>
            <input
              type="text"
              className="re-input"
              value={localLocation}
              onChange={(e) => setLocalLocation(e.target.value)}
              placeholder="e.g., Kitchen, Master Bathroom…"
            />
          </div>

          <div className="re-field">
            <label className="re-label">Photos</label>
            <PhotoUpload
              reportItemId={item.id}
              photos={item.photos}
              onPhotosUpdate={onPhotosUpdate}
            />
          </div>
        </div>
      )}
    </div>
  );
};
